// Identity providers.
// Route: /settings/sso

import { useState } from "react";

import { http } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Banner,
  Confirm,
  CopyButton,
  Dialog,
  DialogFooter,
  Field,
  Toggle,
  useAsync,
  useToast,
} from "../ui";
import { BASE_PATH, errMessage, ErrorBanner } from "./shared";

export interface SSOProviderRow {
  provider: string;
  client_id: string;
  /** Whether a secret is stored. The secret itself never leaves the server. */
  has_secret: boolean;
  is_enabled: boolean;
  allow_auto_create: boolean;
  default_role: string;
  allowed_domains: string;
  auth_url: string | null;
  token_url: string | null;
  user_info_url: string | null;
}

// ============================================================ SSOSettingsView

interface SSOProviderMeta {
  key: string;
  name: string;
  description: string;
  icon: string;
  docUrl: string;
  isCustom?: boolean;
}

const SSO_PROVIDERS: SSOProviderMeta[] = [
  {
    key: "google",
    name: "Google",
    description: "Login with Google accounts",
    icon: "M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z",
    docUrl: "https://console.cloud.google.com/apis/credentials",
  },
  {
    key: "microsoft",
    name: "Microsoft",
    description: "Login with Microsoft work/school accounts",
    icon: "M11 11H3V3h8v8zm10 0h-8V3h8v8zM11 21H3v-8h8v8zm10 0h-8v-8h8v8z",
    docUrl: "https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps",
  },
  {
    key: "github",
    name: "GitHub",
    description: "Login with GitHub accounts",
    icon: "M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z",
    docUrl: "https://github.com/settings/developers",
  },
  {
    key: "facebook",
    name: "Facebook",
    description: "Login with Facebook accounts",
    icon: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
    docUrl: "https://developers.facebook.com/apps",
  },
  {
    key: "custom",
    name: "Custom OIDC",
    description: "Configure a custom OAuth2/OIDC provider",
    icon: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z",
    docUrl: "",
    isCustom: true,
  },
];

function ProviderGlyph({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden focusable="false">
      <path d={path} />
    </svg>
  );
}

interface SSOForm {
  client_id: string;
  client_secret: string;
  is_enabled: boolean;
  allow_auto_create: boolean;
  default_role: string;
  allowed_domains: string;
  auth_url: string;
  token_url: string;
  user_info_url: string;
}

const EMPTY_SSO_FORM: SSOForm = {
  client_id: "",
  client_secret: "",
  is_enabled: false,
  allow_auto_create: false,
  default_role: "agent",
  allowed_domains: "",
  auth_url: "",
  token_url: "",
  user_info_url: "",
};

export function SSOSettingsView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("settings.sso", "write");

  const { data, error, loading, reload } = useAsync<SSOProviderRow[]>(
    () => http.get<SSOProviderRow[]>("/settings/sso"),
    []
  );
  const configured = data ?? [];
  const findConfigured = (key: string) => configured.find((p) => p.provider === key);

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form, setForm] = useState<SSOForm>(EMPTY_SSO_FORM);
  const [saving, setSaving] = useState(false);
  const [toRemove, setToRemove] = useState<string | null>(null);

  const meta = SSO_PROVIDERS.find((p) => p.key === editingKey) ?? null;
  const existing = editingKey ? findConfigured(editingKey) : undefined;

  const redirectUrl = editingKey
    ? `${window.location.origin}${BASE_PATH}/api/auth/sso/${editingKey}/callback`
    : "";

  const openEdit = (key: string) => {
    const current = findConfigured(key);
    setEditingKey(key);
    setForm({
      client_id: current?.client_id ?? "",
      // Never prefilled: the server does not return it.
      client_secret: "",
      is_enabled: current?.is_enabled ?? false,
      allow_auto_create: current?.allow_auto_create ?? false,
      default_role: current?.default_role || "agent",
      allowed_domains: current?.allowed_domains ?? "",
      auth_url: current?.auth_url ?? "",
      token_url: current?.token_url ?? "",
      user_info_url: current?.user_info_url ?? "",
    });
  };

  const save = async () => {
    if (!editingKey) return;
    if (!form.client_id.trim()) {
      toast.push("Enter the client ID", "critical");
      return;
    }
    if (!existing && !form.client_secret.trim()) {
      toast.push("Enter the client secret", "critical");
      return;
    }
    if (
      editingKey === "custom" &&
      (!form.auth_url.trim() || !form.token_url.trim() || !form.user_info_url.trim())
    ) {
      toast.push("A custom provider needs all three endpoint URLs", "critical");
      return;
    }

    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        client_id: form.client_id.trim(),
        is_enabled: form.is_enabled,
        allow_auto_create: form.allow_auto_create,
        default_role: form.default_role,
        allowed_domains: form.allowed_domains.trim(),
      };
      // An empty secret means "keep the stored one", so it is only sent when set.
      if (form.client_secret.trim()) body.client_secret = form.client_secret.trim();
      if (editingKey === "custom") {
        body.auth_url = form.auth_url.trim();
        body.token_url = form.token_url.trim();
        body.user_info_url = form.user_info_url.trim();
      }
      await http.put(`/settings/sso/${editingKey}`, body);
      toast.push("SSO provider saved", "good");
      setEditingKey(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not save the provider"), "critical");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!toRemove) return;
    try {
      await http.del(`/settings/sso/${toRemove}`);
      toast.push("SSO provider removed", "good");
      setToRemove(null);
      setEditingKey(null);
      reload();
    } catch (e) {
      toast.push(errMessage(e, "Could not remove the provider"), "critical");
      throw e;
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Single sign-on</h1>
          <div className="sub">Which identity providers may sign people in to this organisation.</div>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the SSO configuration" />

      <Banner tone="info">
        Configure a provider with the client ID and secret from its console, then paste the redirect
        URL shown in the dialog back into that console. Only enabled providers appear on the login
        screen.
      </Banner>

      {loading ? (
        <div className="grid c3">
          {[0, 1, 2].map((i) => (
            <div className="card" key={i}>
              <div className="skeleton line" />
              <div className="skeleton line short" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid c3">
          {SSO_PROVIDERS.map((p) => {
            const current = findConfigured(p.key);
            return (
              <div className="card" key={p.key}>
                <div className="row">
                  <span aria-hidden>
                    <ProviderGlyph path={p.icon} />
                  </span>
                  <div className="spacer">
                    <div className="strong">{p.name}</div>
                    <div className="muted">{p.description}</div>
                  </div>
                  {current && (
                    <Badge tone={current.is_enabled ? "good" : "neutral"}>
                      {current.is_enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  )}
                </div>
                {current && (
                  <dl className="kv" style={{ marginTop: "var(--sp-3)" }}>
                    <dt>Auto-create users</dt>
                    <dd>{current.allow_auto_create ? "Yes" : "No"}</dd>
                    {current.allowed_domains && (
                      <>
                        <dt>Allowed domains</dt>
                        <dd>{current.allowed_domains}</dd>
                      </>
                    )}
                  </dl>
                )}
                <div className="row" style={{ marginTop: "var(--sp-3)" }}>
                  <button className="btn spacer" disabled={!canWrite} onClick={() => openEdit(p.key)}>
                    {current ? "Configure" : "Set up"}
                  </button>
                  {p.docUrl && (
                    <a
                      className="btn icon"
                      href={p.docUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      title={`${p.name} developer console`}
                      aria-label={`Open the ${p.name} developer console`}
                    >
                      ↗
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        open={editingKey !== null}
        onClose={() => setEditingKey(null)}
        title={`Configure ${meta?.name ?? ""}`}
      >
        <Field
          label="Redirect URL"
          hint={`Paste this into the ${meta?.name ?? "provider"} console as the authorised redirect URI.`}
        >
          <input value={redirectUrl} readOnly className="mono" onFocus={(e) => e.target.select()} />
        </Field>
        <div className="row" style={{ marginBottom: "var(--sp-4)" }}>
          <CopyButton value={redirectUrl} label="Copy redirect URL" />
          <span className="muted">Copy the redirect URL.</span>
        </div>

        <Field label="Client ID" required>
          <input
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
          />
        </Field>
        <Field
          label="Client secret"
          required={!existing}
          hint={existing?.has_secret ? "Leave blank to keep the stored secret." : undefined}
        >
          <input
            type="password"
            value={form.client_secret}
            autoComplete="new-password"
            onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
          />
        </Field>

        {editingKey === "custom" && (
          <>
            <Field label="Authorisation URL" required>
              <input
                value={form.auth_url}
                onChange={(e) => setForm({ ...form, auth_url: e.target.value })}
              />
            </Field>
            <Field label="Token URL" required>
              <input
                value={form.token_url}
                onChange={(e) => setForm({ ...form, token_url: e.target.value })}
              />
            </Field>
            <Field label="User info URL" required>
              <input
                value={form.user_info_url}
                onChange={(e) => setForm({ ...form, user_info_url: e.target.value })}
              />
            </Field>
          </>
        )}

        <div className="setting">
          <div className="text">
            <div className="strong">Enable this provider</div>
            <p>Shows its button on the login screen.</p>
          </div>
          <Toggle
            checked={form.is_enabled}
            ariaLabel="Enable this provider"
            onChange={(v) => setForm({ ...form, is_enabled: v })}
          />
        </div>
        <div className="setting">
          <div className="text">
            <div className="strong">Create users automatically</div>
            <p>A first-time sign-in creates the account instead of being refused.</p>
          </div>
          <Toggle
            checked={form.allow_auto_create}
            ariaLabel="Create users automatically"
            onChange={(v) => setForm({ ...form, allow_auto_create: v })}
          />
        </div>
        {form.allow_auto_create && (
          <Field label="Default role">
            <select
              value={form.default_role}
              onChange={(e) => setForm({ ...form, default_role: e.target.value })}
            >
              <option value="agent">Agent</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="tester">Tester (Test Console only)</option>
            </select>
          </Field>
        )}
        <Field
          label="Allowed email domains"
          hint="Comma-separated. Leave blank to accept any domain the provider returns."
        >
          <input
            value={form.allowed_domains}
            placeholder="example.com, example.org"
            onChange={(e) => setForm({ ...form, allowed_domains: e.target.value })}
          />
        </Field>

        <DialogFooter>
          {existing && (
            <button
              className="btn danger"
              disabled={saving || !canWrite}
              onClick={() => setToRemove(editingKey)}
            >
              Remove
            </button>
          )}
          <span className="spacer" />
          <button className="btn" onClick={() => setEditingKey(null)} disabled={saving}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={saving || !canWrite}>
            {saving && <span className="spinner" />}
            Save
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={toRemove !== null}
        onClose={() => setToRemove(null)}
        onConfirm={remove}
        danger
        title="Remove SSO provider"
        confirmLabel="Remove provider"
        body={
          <>
            The configuration for{" "}
            <strong className="strong">
              {SSO_PROVIDERS.find((p) => p.key === toRemove)?.name ?? toRemove}
            </strong>{" "}
            is deleted, including its stored secret. Anyone who signs in only through it loses that
            route immediately.
          </>
        }
      />
    </div>
  );
}
